/**
 * Item Relationship Rule Engine
 *
 * Evaluates the compound relationship state of an item (or set of items) and
 * returns an ordered list of rule outcomes. Each outcome is one of:
 *   allow | warn | require_approval | block
 * with a named reason code drawn from the canonical list in
 * 07A-item-relationship-interaction-matrix.md §11.
 *
 * Gate priority order (canon 07A §3):
 *   1. Cluster resolution
 *   2. Frame declaration
 *   3. Collection scope completeness
 *   4. Selection integrity (Package atomicity & Exclusive selection)
 *   5. Track role declaration
 *   6. Dependency legitimacy
 *   7. Elaboration selection
 *
 * Lower-priority gates do not fire while a higher-priority gate is blocked.
 */

import {
  elaborationEdgeAppliesToFrame,
  type ItemRelationshipGroup,
  type ItemRelationshipGroupMembership,
  type ItemRelationshipEdge,
  type DependencySubtype,
} from '../types/prioritization/itemRelationships';
import type { ItemFrame } from '../types/prioritization/lifecycle';

// ============================================================================
// Named reason codes (canon 07A §11)
// ============================================================================

export type RuleReasonCode =
  // Gate 1 — Cluster resolution
  | 'CLUSTER_REVIEW_REQUIRED'
  | 'CLUSTER_BLOCKS_PACKAGE'
  // Gate 2 — Frame declaration
  | 'FRAME_DECLARATION_REQUIRED'
  // Gate 3 — Collection scope completeness
  | 'PARTIAL_PACKAGE_IN_SCOPE'
  | 'PARTIAL_AGGREGATION_IN_SCOPE'
  | 'PARTIAL_BREAKDOWN_IN_SCOPE'
  | 'PARTIAL_ELABORATION_IN_SCOPE'
  | 'EXCLUSIVE_CHOICE_PENDING'
  | 'EXTERNAL_HARD_DEPENDENCY'
  /** Item has a hard Prerequisite (upstream enabler) outside the active Collection scope. */
  | 'EXTERNAL_PREREQUISITE'
  | 'GATE_PREREQUISITE_UNMET'
  // Gate 4 — Selection integrity
  | 'EXCLUSIVE_MULTIPLE_SELECTED'
  | 'PACKAGE_MEMBER_HARD_DEP_UNADDRESSED'
  /** A Package member has an unaddressed hard Prerequisite (upstream enabler). */
  | 'PACKAGE_MEMBER_PREREQUISITE_UNADDRESSED'
  | 'REVERSE_DEP_PACKAGE_DESELECT'
  // Gate 5 — Track role
  | 'TRACK_ROLE_REQUIRED'
  // Gate 6 — Dependency legitimacy
  | 'HARD_DEPENDENCY_UNADDRESSED'
  /** Item has a hard Prerequisite (upstream enabler) that has not been addressed. */
  | 'PREREQUISITE_UNADDRESSED'
  // Gate 7 — Elaboration selection
  | 'ELABORATION_SELECTION_REQUIRED'
  // Supplementary checks (no gate order)
  | 'ELABORATION_CHILD_FRAME_MISMATCH'
  /**
   * Both a Prerequisite and a Dependency are asserted on the same item pair.
   * Mirror edges represent the same constraint — one should be removed.
   */
  | 'PREREQUISITE_DEPENDENCY_REDUNDANT'
  /**
   * Both a Breakdown and an Aggregation are asserted on the same parent-child pair.
   * Mirror edges represent the same structure — one should be removed.
   */
  | 'BREAKDOWN_AGGREGATION_REDUNDANT'
  /**
   * Item is declared both as a constituent part of X (Breakdown child of X) and as
   * a Prerequisite for X. These roles are semantically incompatible.
   */
  | 'BREAKDOWN_PREREQUISITE_SEMANTIC_CONFLICT'
  // Post-event review codes
  | 'PACKAGE_REVIEW_REQUIRED_AFTER_FORK'
  | 'AGGREGATION_REVIEW_REQUIRED_AFTER_FORK'
  | 'CLUSTER_REVIEW_DEFERRED_BY_PROTECTION_RING'
  | 'MOST_RESTRICTIVE_RING_APPLIES'
  | 'DISSOLUTION_RELATIONSHIP_REVIEW_REQUIRED'
  | 'DEPENDENCY_INHERITANCE_REVIEW_REQUIRED'
  | 'AGGREGATION_INHERITANCE_REVIEW_REQUIRED'
  | 'ELABORATION_INHERITANCE_REVIEW_REQUIRED'
  | 'COLLECTION_CLUSTER_CLEANUP_REQUIRED'
  | 'SESSION_DISPOSITION_REQUIRED'
  | 'LINEAGE_CONTAINS_CHILDREN_ORPHANED'
  | 'LINEAGE_CONTAINS_REDISTRIBUTION_REQUIRED';

export type RuleOutcomeLevel = 'allow' | 'warn' | 'require_approval' | 'block';

export interface RuleOutcome {
  level: RuleOutcomeLevel;
  reason: RuleReasonCode;
  /** Human-readable description of why this outcome fired. */
  message: string;
  /** Gate priority (1–7). Lower gates block higher ones. */
  gate: 1 | 2 | 3 | 4 | 5 | 6 | 7;
  /** Item IDs directly involved in this outcome. */
  involvedItemIds: string[];
  /** Relationship IDs (group or edge) involved, if applicable. */
  involvedRelationshipIds?: string[];
}

// ============================================================================
// Evaluation context
// ============================================================================

/**
 * The full relationship context needed to evaluate an item's compound state.
 *
 * Callers provide:
 *   - itemId: the item being evaluated
 *   - allGroups / allMemberships / allEdges: the full workspace relationship graph
 *   - scopeItemIds: the set of item IDs in the active prioritization scope
 *     (i.e., the Collection type-B being used). Pass null when not evaluating
 *     within a bounded scope.
 *   - activeFrameId: the frame declared for this item in the current track
 *     context. Pass null when no frame has been declared.
 *   - declaredTrackRole: 'aggregation' | 'elaboration' | 'both' | null.
 *     Required when item is both an aggregation parent and an elaboration parent.
 *   - selectedElaborationChildId: the elaboration child selected as the
 *     committed approach. Pass null if no selection has been made.
 *   - ringAtTime: the most restrictive protection ring the item is currently in.
 */
export interface RelationshipEvalContext {
  itemId: string;
  allGroups: ItemRelationshipGroup[];
  allMemberships: ItemRelationshipGroupMembership[];
  allEdges: ItemRelationshipEdge[];
  /** Item frames from the item record (item.frames). */
  itemFrames: ItemFrame[];
  /** Active scope item IDs (type-B Collection members). Null = no bounded scope. */
  scopeItemIds: Set<string> | null;
  /** Active frame ID declared for this context. Null = not declared. */
  activeFrameId: string | null;
  /**
   * The item's own category (its native frame type, e.g. 'opportunity'). Used to
   * resolve the active frame type for frame-contextualized elaboration when the
   * item's own category is the lead framing (itemOwnFrameIsCanonical). Optional;
   * when omitted, the own-category lead resolves to no specific frame type.
   */
  itemCategory?: string | null;
  /**
   * Whether the item is currently using its own category as the canonical
   * framing (i.e. canonicalFrameId is null/undefined and itemCategory is set).
   * When true, Gate 2 treats this as a valid frame declaration.
   */
  itemOwnFrameIsCanonical?: boolean;
  /** Track role declared for dual aggregation+elaboration parents. */
  declaredTrackRole: 'aggregation' | 'elaboration' | 'both' | null;
  /** Selected elaboration child ID at commitment time. */
  selectedElaborationChildId: string | null;
  /** Most restrictive protection ring. 1 = active session, 2 = in-track, 3 = open. */
  ringAtTime: 1 | 2 | 3;
  /** Whether this evaluation is at commitment time (vs. inclusion/track-entry time). */
  isCommitmentTime: boolean;
  /**
   * Item IDs that have received (or are receiving) a commitment-quality
   * disposition in the current context. Used by the Exclusive gate to detect
   * "one or the other, not both" violations: if two members of an exclusive
   * group both appear here, committing the second is blocked. Pass null/omit
   * when commitment selection state is unknown.
   */
  committedItemIds?: Set<string> | null;
}

// ============================================================================
// Helpers
// ============================================================================

function getGroupsForItem(
  itemId: string,
  groups: ItemRelationshipGroup[],
  memberships: ItemRelationshipGroupMembership[],
): ItemRelationshipGroup[] {
  const memberGroupIds = new Set(
    memberships.filter((m) => m.itemId === itemId).map((m) => m.groupId),
  );
  return groups.filter((g) => memberGroupIds.has(g.id) && g.status !== 'dissolved');
}

function getMembersOfGroup(
  groupId: string,
  memberships: ItemRelationshipGroupMembership[],
): string[] {
  return memberships.filter((m) => m.groupId === groupId).map((m) => m.itemId);
}

/** Walk the aggregation tree downward from parentId, returning all child item IDs. */
function getAggregationChildIds(
  parentId: string,
  edges: ItemRelationshipEdge[],
  visited = new Set<string>(),
): string[] {
  if (visited.has(parentId)) return [];
  visited.add(parentId);
  const children = edges
    .filter((e) => e.type === 'aggregation' && e.sourceItemId === parentId && e.status !== 'dissolved')
    .map((e) => e.targetItemId);
  return [
    ...children,
    ...children.flatMap((childId) => getAggregationChildIds(childId, edges, visited)),
  ];
}

/** Walk the breakdown tree downward from parentId, returning all child item IDs. */
export function getBreakdownChildIds(
  parentId: string,
  edges: ItemRelationshipEdge[],
  visited = new Set<string>(),
): string[] {
  if (visited.has(parentId)) return [];
  visited.add(parentId);
  const children = edges
    .filter((e) => e.type === 'breakdown' && e.sourceItemId === parentId && e.status !== 'dissolved')
    .map((e) => e.targetItemId);
  return [
    ...children,
    ...children.flatMap((childId) => getBreakdownChildIds(childId, edges, visited)),
  ];
}

/**
 * Returns all composition child IDs reachable from parentId via either
 * aggregation (bottom-up composition) or breakdown (top-down decomposition).
 * Both relationship types describe the same whole-part tree; rules that apply
 * to aggregation children also apply to breakdown children.
 */
export function getCompositionChildIds(
  parentId: string,
  edges: ItemRelationshipEdge[],
): string[] {
  return [
    ...getAggregationChildIds(parentId, edges),
    ...getBreakdownChildIds(parentId, edges),
  ];
}

/** Return all elaboration child item IDs for a given parent (frame-agnostic). */
function getElaborationChildIds(parentId: string, edges: ItemRelationshipEdge[]): string[] {
  return edges
    .filter((e) => e.type === 'elaboration' && e.sourceItemId === parentId && e.status !== 'dissolved')
    .map((e) => e.targetItemId);
}

/**
 * Resolve the parent's active frame type for this evaluation context.
 * - an explicitly declared alternate frame → that frame's type
 * - the item's own category is the lead framing → the item's category
 * - otherwise undeclared → null
 */
function resolveActiveFrameType(ctx: RelationshipEvalContext): string | null {
  if (ctx.activeFrameId) {
    const frame = ctx.itemFrames.find((f) => f.id === ctx.activeFrameId);
    return frame?.frameType ?? null;
  }
  if (ctx.itemOwnFrameIsCanonical) return ctx.itemCategory ?? null;
  return null;
}

/**
 * Return the elaboration child IDs that apply under the parent's active frame
 * (canon 07 Rule 24). Universal (unscoped) edges always apply; frame-scoped
 * edges apply only when their scope includes the active frame type. This is the
 * frame-aware replacement for getElaborationChildIds at the gate call sites.
 */
function getApplicableElaborationChildIds(
  parentId: string,
  edges: ItemRelationshipEdge[],
  activeFrameType: string | null,
): string[] {
  return edges
    .filter((e) => e.type === 'elaboration' && e.sourceItemId === parentId && e.status !== 'dissolved')
    .filter((e) => elaborationEdgeAppliesToFrame(e, activeFrameType))
    .map((e) => e.targetItemId);
}

/** Return all hard-dependency target IDs reachable from a given item (direct only). */
function getHardDependencyTargets(itemId: string, edges: ItemRelationshipEdge[]): string[] {
  return edges
    .filter(
      (e) =>
        e.type === 'dependency' &&
        e.sourceItemId === itemId &&
        (e.subtype as DependencySubtype) === 'hard' &&
        e.status !== 'dissolved',
    )
    .map((e) => e.targetItemId);
}

/**
 * Return all hard-prerequisite source IDs for a given item (direct only).
 *
 * A prerequisite edge goes from upstream enabler (sourceItemId) to enabled item
 * (targetItemId). When an item is the *target* of a hard prerequisite, the source
 * is an upstream enabler that must be addressed before this item can be committed.
 * This is the symmetric counterpart of getHardDependencyTargets.
 */
function getHardPrerequisiteSources(itemId: string, edges: ItemRelationshipEdge[]): string[] {
  return edges
    .filter(
      (e) =>
        e.type === 'prerequisite' &&
        e.targetItemId === itemId &&
        (e.subtype as DependencySubtype) === 'hard' &&
        e.status !== 'dissolved',
    )
    .map((e) => e.sourceItemId);
}

/**
 * Return the candidacy-gate ("gate" subtype) target IDs that the given item
 * requires to be present in the active scope to be a valid pool candidate.
 *
 * Covers both assertion directions:
 * - dependency edges where the item is the source (item depends on target)
 * - prerequisite edges where the item is the target (item is enabled by source)
 */
function getGatePrerequisiteTargets(itemId: string, edges: ItemRelationshipEdge[]): string[] {
  const fromDependency = edges
    .filter(
      (e) =>
        e.type === 'dependency' &&
        e.sourceItemId === itemId &&
        (e.subtype as DependencySubtype) === 'gate' &&
        e.status !== 'dissolved',
    )
    .map((e) => e.targetItemId);
  const fromPrerequisite = edges
    .filter(
      (e) =>
        e.type === 'prerequisite' &&
        e.targetItemId === itemId &&
        (e.subtype as DependencySubtype) === 'gate' &&
        e.status !== 'dissolved',
    )
    .map((e) => e.sourceItemId);
  return [...fromDependency, ...fromPrerequisite];
}

/**
 * Determine whether an item has multiple frames and whether a "response-oriented"
 * frame (solution) is present. Used for the elaboration-child frame-mismatch check.
 */
function isResponseOrientedFrame(frameType: string): boolean {
  return frameType === 'solution';
}

// ============================================================================
// Gate evaluators
// ============================================================================

/**
 * Gate 1 — Cluster resolution.
 *
 * If the item is a member of any unresolved (candidate) cluster group,
 * return a block. If any sibling Bloc member is in an unresolved cluster,
 * return a block with CLUSTER_BLOCKS_PACKAGE.
 */
function evaluateGate1Cluster(ctx: RelationshipEvalContext): RuleOutcome[] {
  const outcomes: RuleOutcome[] = [];
  const groups = getGroupsForItem(ctx.itemId, ctx.allGroups, ctx.allMemberships);
  const clusterCandidates = groups.filter(
    (g) => g.type === 'cluster' && g.status === 'candidate',
  );

  for (const cluster of clusterCandidates) {
    outcomes.push({
      level: 'block',
      reason: 'CLUSTER_REVIEW_REQUIRED',
      message: `Item is in an unresolved cluster group "${cluster.title ?? cluster.id}". Cluster review must complete before this item can participate in any prioritization action.`,
      gate: 1,
      involvedItemIds: [ctx.itemId],
      involvedRelationshipIds: [cluster.id],
    });
  }

  // Also check: is this item in a Package where any OTHER Package member has an unresolved cluster?
  const packageGroups = groups.filter((g) => g.type === 'package' && g.status !== 'dissolved');
  for (const pkg of packageGroups) {
    const memberIds = getMembersOfGroup(pkg.id, ctx.allMemberships);
    for (const memberId of memberIds) {
      if (memberId === ctx.itemId) continue;
      const memberGroups = getGroupsForItem(memberId, ctx.allGroups, ctx.allMemberships);
      const memberClusterCandidates = memberGroups.filter(
        (g) => g.type === 'cluster' && g.status === 'candidate',
      );
      if (memberClusterCandidates.length > 0) {
        outcomes.push({
          level: 'block',
          reason: 'CLUSTER_BLOCKS_PACKAGE',
          message: `Package "${pkg.title ?? pkg.id}" contains item ${memberId} which is in an unresolved cluster. The Package cannot be committed while a member's identity is ambiguous.`,
          gate: 1,
          involvedItemIds: [ctx.itemId, memberId],
          involvedRelationshipIds: [pkg.id, ...memberClusterCandidates.map((g) => g.id)],
        });
      }
    }
  }

  return outcomes;
}

/**
 * Gate 2 — Frame declaration.
 *
 * If the item has multiple frames and no active frame has been declared
 * for this context, block track entry.
 */
function evaluateGate2Frame(ctx: RelationshipEvalContext): RuleOutcome[] {
  if (ctx.itemFrames.length <= 1) return [];
  // Pass when an explicit alternate frame is declared OR the item's own category
  // is the active framing (canonicalFrameId is null = item-own is canonical).
  if (ctx.activeFrameId !== null) return [];
  if (ctx.itemOwnFrameIsCanonical) return [];

  return [
    {
      level: 'block',
      reason: 'FRAME_DECLARATION_REQUIRED',
      message: `Item has ${ctx.itemFrames.length} categories but no active category has been declared for this prioritization context. Declare a category before Track entry.`,
      gate: 2,
      involvedItemIds: [ctx.itemId],
    },
  ];
}

/**
 * Gate 3 — Collection scope completeness.
 *
 * Only fires when a type-B scope (scopeItemIds) is active.
 * Checks for:
 *   - Partial Package representation → warn (owner ruling 2026-10-12: flagged, never disallowed)
 *   - Partial aggregation children → warn
 *   - Partial elaboration options → warn
 *   - Hard deps pointing outside scope → warn (block at commitment time)
 */
function evaluateGate3CollectionScope(ctx: RelationshipEvalContext): RuleOutcome[] {
  if (!ctx.scopeItemIds) return [];
  const outcomes: RuleOutcome[] = [];
  const scope = ctx.scopeItemIds;

  // 3a. Partial Package in scope
  const groups = getGroupsForItem(ctx.itemId, ctx.allGroups, ctx.allMemberships);
  const packageGroups = groups.filter((g) => g.type === 'package' && g.status !== 'dissolved');
  for (const pkg of packageGroups) {
    const memberIds = getMembersOfGroup(pkg.id, ctx.allMemberships);
    const missingMembers = memberIds.filter((id) => id !== ctx.itemId && !scope.has(id));
    if (missingMembers.length > 0) {
      outcomes.push({
        level: 'warn',
        reason: 'PARTIAL_PACKAGE_IN_SCOPE',
        message: `Part of package "${pkg.title ?? pkg.id}". Members [${missingMembers.join(', ')}] are ranked elsewhere. The package holds only if every member is chosen.`,
        gate: 3,
        involvedItemIds: [ctx.itemId, ...missingMembers],
        involvedRelationshipIds: [pkg.id],
      });
    }
  }

  // 3b. Partial aggregation children
  const aggChildIds = getAggregationChildIds(ctx.itemId, ctx.allEdges);
  if (aggChildIds.length > 0) {
    const missingChildren = aggChildIds.filter((id) => !scope.has(id));
    if (missingChildren.length > 0) {
      outcomes.push({
        level: 'warn',
        reason: 'PARTIAL_AGGREGATION_IN_SCOPE',
        message: `Item is an aggregation parent. Children [${missingChildren.join(', ')}] are not in this Collection scope. The aggregation structure is partially visible.`,
        gate: 3,
        involvedItemIds: [ctx.itemId, ...missingChildren],
      });
    }
  }

  // 3b-2. Partial breakdown children
  const brkChildIds = getBreakdownChildIds(ctx.itemId, ctx.allEdges);
  if (brkChildIds.length > 0) {
    const missingBrkChildren = brkChildIds.filter((id) => !scope.has(id));
    if (missingBrkChildren.length > 0) {
      outcomes.push({
        level: 'warn',
        reason: 'PARTIAL_BREAKDOWN_IN_SCOPE',
        message: `Item is a breakdown parent. Constituent parts [${missingBrkChildren.join(', ')}] are not in this Collection scope. The breakdown structure is partially visible.`,
        gate: 3,
        involvedItemIds: [ctx.itemId, ...missingBrkChildren],
      });
    }
  }

  // 3c. Partial elaboration options — only options that apply under the parent's
  // active frame count toward completeness (canon 07 Rule 24).
  const activeFrameType = resolveActiveFrameType(ctx);
  const elChildIds = getApplicableElaborationChildIds(ctx.itemId, ctx.allEdges, activeFrameType);
  if (elChildIds.length > 0) {
    const missingOptions = elChildIds.filter((id) => !scope.has(id));
    if (missingOptions.length > 0) {
      const frameNote = activeFrameType ? ` for the "${activeFrameType}" category` : '';
      outcomes.push({
        level: 'warn',
        reason: 'PARTIAL_ELABORATION_IN_SCOPE',
        message: `Item is an elaboration parent with ${elChildIds.length} options${frameNote}. [${missingOptions.join(', ')}] are not in this Collection. The solution option set is incomplete.`,
        gate: 3,
        involvedItemIds: [ctx.itemId, ...missingOptions],
      });
    }
  }

  // 3c-2. Exclusive (Competing) — multiple members of a "one or the other,
  // not both" group present in the same scope. This is NOT a partial-scope
  // problem (a single member in scope is fine); it is a notice that a mutually
  // exclusive choice exists and at most one may be committed. Warn, not block.
  const exclusiveGroups = groups.filter((g) => g.type === 'exclusive' && g.status !== 'dissolved');
  for (const ex of exclusiveGroups) {
    const memberIds = getMembersOfGroup(ex.id, ctx.allMemberships);
    const inScopeMembers = memberIds.filter((id) => scope.has(id));
    if (inScopeMembers.length >= 2) {
      outcomes.push({
        level: 'warn',
        reason: 'EXCLUSIVE_CHOICE_PENDING',
        message: `Exclusive group "${ex.title ?? ex.id}" has ${inScopeMembers.length} competing members in this scope. These are mutually exclusive — at most one may receive a commitment-quality outcome ("one or the other, not both").`,
        gate: 3,
        involvedItemIds: inScopeMembers,
        involvedRelationshipIds: [ex.id],
      });
    }
  }

  // 3d. Hard deps pointing outside scope
  const hardDepTargets = getHardDependencyTargets(ctx.itemId, ctx.allEdges);
  const externalDeps = hardDepTargets.filter((id) => !scope.has(id));
  for (const depTargetId of externalDeps) {
    const level: RuleOutcomeLevel = ctx.isCommitmentTime ? 'block' : 'warn';
    outcomes.push({
      level,
      reason: 'EXTERNAL_HARD_DEPENDENCY',
      message: `Item has a hard dependency on item ${depTargetId} which is outside this Collection scope. ${ctx.isCommitmentTime ? 'Commitment-quality disposition is blocked.' : 'Address before commitment.'}`,
      gate: 3,
      involvedItemIds: [ctx.itemId, depTargetId],
    });
  }

  // 3d-2. Hard prerequisites pointing outside scope (symmetric to 3d).
  // A Prerequisite edge goes from upstream enabler → enabled item. When the
  // source of a hard prerequisite is outside scope, the enabler is absent and
  // should be addressed before committing this item (canon §4.38 EX-PRE-1,
  // mirroring the dep handling above for the symmetric assertion direction).
  const hardPrereqSources = getHardPrerequisiteSources(ctx.itemId, ctx.allEdges);
  const externalPrereqs = hardPrereqSources.filter((id) => !scope.has(id));
  for (const prereqSourceId of externalPrereqs) {
    const level: RuleOutcomeLevel = ctx.isCommitmentTime ? 'block' : 'warn';
    outcomes.push({
      level,
      reason: 'EXTERNAL_PREREQUISITE',
      message: `Item has a hard Prerequisite (upstream enabler) — item ${prereqSourceId} — which is outside this Collection scope. ${ctx.isCommitmentTime ? 'Commitment-quality disposition is blocked.' : 'Address before commitment.'}`,
      gate: 3,
      involvedItemIds: [ctx.itemId, prereqSourceId],
    });
  }

  // 3e. Candidacy gate prerequisites ("gate" subtype) — the item may not be a
  // valid candidate in this pool unless the gate target is also in scope.
  // Owner ruling 2026-10-12: flagged, never disallowed. The item it needs is
  // usually another team's to rank, so it is either included or the flag is
  // carried — the same as a partial Package. Canon 12 §5.4, Rules 17 and 28.
  const gateTargets = getGatePrerequisiteTargets(ctx.itemId, ctx.allEdges);
  const unmetGates = gateTargets.filter((id) => !scope.has(id));
  for (const targetId of unmetGates) {
    outcomes.push({
      level: 'warn',
      reason: 'GATE_PREREQUISITE_UNMET',
      message: `Item needs item ${targetId}, which is not in this scope. Include it here, or it must be chosen wherever it is ranked.`,
      gate: 3,
      involvedItemIds: [ctx.itemId, targetId],
    });
  }

  return outcomes;
}

/**
 * Gate 4 — Selection integrity (Package atomicity & Exclusive selection).
 *
 * When evaluating at commitment time, verify all Package members are in scope.
 * Also surface reverse-dependency warning: if de-selecting a Package, items
 * outside the Package that have hard deps on Package members must be surfaced.
 *
 * Exclusive (Competing) is the dual of Package atomicity: Package = all-or-
 * nothing, Exclusive = at-most-one. If two members of an exclusive group are
 * both being committed (present in ctx.committedItemIds), committing this item
 * is blocked — "one or the other, not both."
 *
 * Note: the partial-Package-in-scope case is already handled in Gate 3 when a
 * Collection scope is active. Gate 4 catches cases without a bounded scope
 * where the commitment is being made directly.
 */
function evaluateGate4SelectionIntegrity(ctx: RelationshipEvalContext): RuleOutcome[] {
  if (!ctx.isCommitmentTime) return [];
  const outcomes: RuleOutcome[] = [];
  const groups = getGroupsForItem(ctx.itemId, ctx.allGroups, ctx.allMemberships);
  const packageGroups = groups.filter((g) => g.type === 'package' && g.status !== 'dissolved');

  // Exclusive selection conflict — "one or the other, not both".
  const committed = ctx.committedItemIds;
  if (committed) {
    const exclusiveGroups = groups.filter((g) => g.type === 'exclusive' && g.status !== 'dissolved');
    for (const ex of exclusiveGroups) {
      const memberIds = getMembersOfGroup(ex.id, ctx.allMemberships);
      const committedSiblings = memberIds.filter((id) => id !== ctx.itemId && committed.has(id));
      if (committedSiblings.length > 0) {
        outcomes.push({
          level: 'block',
          reason: 'EXCLUSIVE_MULTIPLE_SELECTED',
          message: `Exclusive group "${ex.title ?? ex.id}" is mutually exclusive ("one or the other, not both"), but member(s) [${committedSiblings.join(', ')}] are already committed. Committing this item would select more than one competing alternative.`,
          gate: 4,
          involvedItemIds: [ctx.itemId, ...committedSiblings],
          involvedRelationshipIds: [ex.id],
        });
      }
    }
  }

  for (const pkg of packageGroups) {
    const memberIds = getMembersOfGroup(pkg.id, ctx.allMemberships);

    // Hard deps on any Package member (from outside the Package)
    const pkgMemberSet = new Set(memberIds);
    for (const memberId of memberIds) {
      const memberHardDepTargets = getHardDependencyTargets(memberId, ctx.allEdges);
      const externalHardDeps = memberHardDepTargets.filter((id) => !pkgMemberSet.has(id));
      for (const depTargetId of externalHardDeps) {
        // Skip if already caught by Gate 3 (scope active) to avoid duplication
        if (ctx.scopeItemIds && !ctx.scopeItemIds.has(depTargetId)) continue;
        outcomes.push({
          level: 'block',
          reason: 'PACKAGE_MEMBER_HARD_DEP_UNADDRESSED',
          message: `Package member ${memberId} has a hard dependency on item ${depTargetId} which has not been addressed. The Package cannot receive a commitment-quality outcome.`,
          gate: 4,
          involvedItemIds: [ctx.itemId, memberId, depTargetId],
          involvedRelationshipIds: [pkg.id],
        });
      }
    }

    // Reverse dep surfacing: items outside the Package that depend on Package members
    // Surface as warn when a Package is being de-selected.
    for (const memberId of memberIds) {
      const reverseDeps = ctx.allEdges.filter(
        (e) =>
          e.type === 'dependency' &&
          (e.subtype as DependencySubtype) === 'hard' &&
          e.targetItemId === memberId &&
          !pkgMemberSet.has(e.sourceItemId) &&
          e.status !== 'dissolved',
      );
      for (const dep of reverseDeps) {
        outcomes.push({
          level: 'warn',
          reason: 'REVERSE_DEP_PACKAGE_DESELECT',
          message: `Item ${dep.sourceItemId} has a hard dependency on Package member ${memberId}. De-selecting this Package will block that dependent item from a legitimate commitment.`,
          gate: 4,
          involvedItemIds: [ctx.itemId, dep.sourceItemId, memberId],
          involvedRelationshipIds: [pkg.id, dep.id],
        });
      }
    }
  }

  return outcomes;
}

/**
 * Gate 5 — Track role declaration.
 *
 * If an item is both an aggregation parent and an elaboration parent and
 * no track role has been declared, block track entry.
 */
function evaluateGate5TrackRole(ctx: RelationshipEvalContext): RuleOutcome[] {
  const isCompositionParent = ctx.allEdges.some(
    (e) =>
      (e.type === 'aggregation' || e.type === 'breakdown') &&
      e.sourceItemId === ctx.itemId &&
      e.status !== 'dissolved',
  );
  const isElParent = ctx.allEdges.some(
    (e) => e.type === 'elaboration' && e.sourceItemId === ctx.itemId && e.status !== 'dissolved',
  );

  if (!isCompositionParent || !isElParent) return [];
  if (ctx.declaredTrackRole !== null) return [];

  return [
    {
      level: 'block',
      reason: 'TRACK_ROLE_REQUIRED',
      message: `Item is both a composition parent (aggregation or breakdown) and an elaboration parent (a need with a possibility continuum). A track role must be declared before this item can enter a Track: 'aggregation', 'elaboration', or 'both'.`,
      gate: 5,
      involvedItemIds: [ctx.itemId],
    },
  ];
}

/**
 * Gate 6 — Dependency legitimacy.
 *
 * At commitment time, surface unaddressed hard dependencies.
 * Deps on aggregation children always surface.
 * Deps on elaboration children surface only when that child is the selected approach.
 */
function evaluateGate6Dependency(ctx: RelationshipEvalContext): RuleOutcome[] {
  if (!ctx.isCommitmentTime) return [];
  const outcomes: RuleOutcome[] = [];

  const hardDepTargets = getHardDependencyTargets(ctx.itemId, ctx.allEdges);
  const activeFrameType = resolveActiveFrameType(ctx);
  const allElChildIds = getElaborationChildIds(ctx.itemId, ctx.allEdges);
  const applicableElChildIds = getApplicableElaborationChildIds(ctx.itemId, ctx.allEdges, activeFrameType);

  for (const targetId of hardDepTargets) {
    // Check if the target is an elaboration child of this item
    const isElaborationChild = allElChildIds.includes(targetId);

    if (isElaborationChild) {
      // An option that does not apply under the active frame is not on the table
      // for this context (canon 07 Rule 24) — its dependency is irrelevant here.
      if (!applicableElChildIds.includes(targetId)) continue;
      // Elaboration child deps only fire when that child is selected
      if (ctx.selectedElaborationChildId !== targetId) continue;
    }

    // Check if the target is a composition child (aggregation or breakdown) — those always surface
    const compositionChildIds = getCompositionChildIds(ctx.itemId, ctx.allEdges);
    const isCompositionChild = compositionChildIds.includes(targetId);

    // Surface if: not an elaboration-only dep, OR composition child, OR selected el child
    if (!isElaborationChild || isCompositionChild || ctx.selectedElaborationChildId === targetId) {
      outcomes.push({
        level: 'block',
        reason: 'HARD_DEPENDENCY_UNADDRESSED',
        message: `Item has a hard dependency on item ${targetId} that has not been addressed in this prioritization context. Commitment-quality disposition is blocked until this dependency condition is resolved.`,
        gate: 6,
        involvedItemIds: [ctx.itemId, targetId],
      });
    }
  }

  // Also check: composition children's hard deps (aggregation + breakdown, walk tree)
  const compositionChildIds = getCompositionChildIds(ctx.itemId, ctx.allEdges);
  for (const childId of compositionChildIds) {
    const childHardDeps = getHardDependencyTargets(childId, ctx.allEdges);
    for (const depTargetId of childHardDeps) {
      outcomes.push({
        level: 'block',
        reason: 'HARD_DEPENDENCY_UNADDRESSED',
        message: `Composition child ${childId} has a hard dependency on item ${depTargetId}. Committing the parent item implies this child is in scope; the dependency must be addressed.`,
        gate: 6,
        involvedItemIds: [ctx.itemId, childId, depTargetId],
      });
    }
  }

  // Hard prerequisites on this item (upstream enablers not yet addressed).
  // A Prerequisite edge goes from upstream enabler (sourceItemId) → enabled item
  // (targetItemId = ctx.itemId). A hard prerequisite means the enabler must be
  // addressed before this commitment is valid (symmetric to HARD_DEPENDENCY_UNADDRESSED,
  // canon §4.38 EX-PRE-1 / §4.37 EX-DEP-1 mirror).
  const hardPrereqSources = getHardPrerequisiteSources(ctx.itemId, ctx.allEdges);
  for (const prereqSourceId of hardPrereqSources) {
    outcomes.push({
      level: 'block',
      reason: 'PREREQUISITE_UNADDRESSED',
      message: `Item has a hard Prerequisite on item ${prereqSourceId} (upstream enabler) that has not been addressed in this prioritization context. Commitment-quality disposition is blocked until this prerequisite condition is resolved.`,
      gate: 6,
      involvedItemIds: [ctx.itemId, prereqSourceId],
    });
  }

  return outcomes;
}

/**
 * Gate 7 — Elaboration selection.
 *
 * At commitment time, if item is an elaboration parent and no child has been
 * selected, require selection.
 *
 * Note: this gate only applies when the declared track role is 'elaboration'
 * or 'both', or when no role is declared but the item is an elaboration parent.
 */
function evaluateGate7ElaborationSelection(ctx: RelationshipEvalContext): RuleOutcome[] {
  if (!ctx.isCommitmentTime) return [];

  // Only options that apply under the parent's active frame require selection
  // (canon 07 Rule 24). A frame with no applicable options needs no selection.
  const activeFrameType = resolveActiveFrameType(ctx);
  const applicableChildIds = getApplicableElaborationChildIds(ctx.itemId, ctx.allEdges, activeFrameType);
  if (applicableChildIds.length === 0) return [];

  // If role is 'aggregation' only, elaboration selection is not required
  if (ctx.declaredTrackRole === 'aggregation') return [];

  if (ctx.selectedElaborationChildId !== null) return [];

  const frameNote = activeFrameType ? ` under the "${activeFrameType}" category` : '';
  return [
    {
      level: 'require_approval',
      reason: 'ELABORATION_SELECTION_REQUIRED',
      message: `Item is an elaboration parent. A specific elaboration child (approach)${frameNote} must be selected before this commitment is complete.`,
      gate: 7,
      involvedItemIds: [ctx.itemId],
    },
  ];
}

// ============================================================================
// Supplementary checks (not gate-ordered but important for completeness)
// ============================================================================

/**
 * Elaboration child frame mismatch.
 *
 * When an elaboration child's primary frame is not solution- or fulfillment-oriented,
 * flag for review. This is not a gate but a warn.
 */
function checkElaborationChildFrameMismatch(
  ctx: RelationshipEvalContext,
): RuleOutcome[] {
  const outcomes: RuleOutcome[] = [];

  // Is this item an elaboration child?
  const parentEdges = ctx.allEdges.filter(
    (e) => e.type === 'elaboration' && e.targetItemId === ctx.itemId && e.status !== 'dissolved',
  );
  if (parentEdges.length === 0) return [];

  // Check if the item's primary frame is not solution- or fulfillment-oriented
  const primaryFrame = ctx.itemFrames.find((f) => f.isCanonical) ?? ctx.itemFrames[0];
  if (primaryFrame && !isResponseOrientedFrame(primaryFrame.frameType)) {
    outcomes.push({
      level: 'warn',
      reason: 'ELABORATION_CHILD_FRAME_MISMATCH',
      message: `This item is an elaboration possibility, but its primary category is "${primaryFrame.frameType}", which is not solution- or fulfillment-oriented. Review whether it still belongs on this continuum.`,
      gate: 3, // surfaced alongside scope checks
      involvedItemIds: [ctx.itemId],
      involvedRelationshipIds: parentEdges.map((e) => e.id),
    });
  }

  return outcomes;
}

// ============================================================================
// Main evaluator
// ============================================================================

export interface RelationshipEvalResult {
  /** The most severe level across all outcomes. 'allow' if no outcomes fire. */
  summary: RuleOutcomeLevel;
  /** All individual rule outcomes. */
  outcomes: RuleOutcome[];
  /** True if any gate at priority 1–4 is blocked (hard blockers). */
  isHardBlocked: boolean;
  /**
   * The highest-priority gate that produced a block.
   * null if not blocked.
   */
  blockedAtGate: 1 | 2 | 3 | 4 | 5 | 6 | 7 | null;
}

/**
 * Evaluate all relationship rules for a single item in context.
 *
 * Gates are evaluated in priority order. If gate N produces a block, gates
 * N+1 through 7 are still evaluated (to provide complete diagnostics), but
 * the `blockedAtGate` field will reflect the highest-priority (lowest number)
 * gate that fired a block.
 *
 * This ensures the UI can show all issues at once while still conveying
 * priority ordering.
 */
export function evaluateItemRelationships(ctx: RelationshipEvalContext): RelationshipEvalResult {
  const allOutcomes: RuleOutcome[] = [
    ...evaluateGate1Cluster(ctx),
    ...evaluateGate2Frame(ctx),
    ...evaluateGate3CollectionScope(ctx),
    ...evaluateGate4SelectionIntegrity(ctx),
    ...evaluateGate5TrackRole(ctx),
    ...evaluateGate6Dependency(ctx),
    ...evaluateGate7ElaborationSelection(ctx),
    ...checkElaborationChildFrameMismatch(ctx),
  ];

  const levelOrder: RuleOutcomeLevel[] = ['allow', 'warn', 'require_approval', 'block'];

  const summary: RuleOutcomeLevel =
    allOutcomes.length === 0
      ? 'allow'
      : allOutcomes.reduce<RuleOutcomeLevel>((worst, o) => {
          return levelOrder.indexOf(o.level) > levelOrder.indexOf(worst) ? o.level : worst;
        }, 'allow');

  const blocks = allOutcomes.filter((o) => o.level === 'block');
  const blockedAtGate = blocks.length > 0
    ? (Math.min(...blocks.map((o) => o.gate)) as 1 | 2 | 3 | 4 | 5 | 6 | 7)
    : null;

  return {
    summary,
    outcomes: allOutcomes,
    isHardBlocked: blockedAtGate !== null && blockedAtGate <= 4,
    blockedAtGate,
  };
}

// ============================================================================
// Convenience: evaluate dissolution readiness
// ============================================================================

/**
 * Check whether an item's active relationships have all been reviewed,
 * making it safe to proceed with a lineage dissolution event.
 *
 * Returns block outcomes for each unresolved relationship category.
 */
export function evaluateDissolutionReadiness(
  itemId: string,
  groups: ItemRelationshipGroup[],
  memberships: ItemRelationshipGroupMembership[],
  edges: ItemRelationshipEdge[],
): RuleOutcome[] {
  const outcomes: RuleOutcome[] = [];

  // Active clusters
  const itemGroups = getGroupsForItem(itemId, groups, memberships);
  const activeClusters = itemGroups.filter((g) => g.type === 'cluster' && g.status !== 'dissolved');
  if (activeClusters.length > 0) {
    outcomes.push({
      level: 'block',
      reason: 'DISSOLUTION_RELATIONSHIP_REVIEW_REQUIRED',
      message: `Item has ${activeClusters.length} active cluster relationship(s) that must be resolved before dissolution. Resolve clusters first (step 1 of dissolution sequence).`,
      gate: 1,
      involvedItemIds: [itemId],
      involvedRelationshipIds: activeClusters.map((g) => g.id),
    });
  }

  // Active packages
  const activePackages = itemGroups.filter((g) => g.type === 'package' && g.status !== 'dissolved');
  if (activePackages.length > 0) {
    outcomes.push({
      level: 'block',
      reason: 'DISSOLUTION_RELATIONSHIP_REVIEW_REQUIRED',
      message: `Item is a member of ${activePackages.length} active Package(s). Dissolve or restructure each Package before dissolution (step 2 of dissolution sequence).`,
      gate: 1,
      involvedItemIds: [itemId],
      involvedRelationshipIds: activePackages.map((g) => g.id),
    });
  }

  // Active exclusive (Competing) groups
  const activeExclusives = itemGroups.filter((g) => g.type === 'exclusive' && g.status !== 'dissolved');
  if (activeExclusives.length > 0) {
    outcomes.push({
      level: 'block',
      reason: 'DISSOLUTION_RELATIONSHIP_REVIEW_REQUIRED',
      message: `Item is a member of ${activeExclusives.length} active Exclusive (Competing) group(s). Dissolve or restructure each before dissolution so the remaining competing alternatives are reviewed.`,
      gate: 1,
      involvedItemIds: [itemId],
      involvedRelationshipIds: activeExclusives.map((g) => g.id),
    });
  }

  // Active composition edges (aggregation + breakdown)
  const activeCompositionEdges = edges.filter(
    (e) =>
      (e.type === 'aggregation' || e.type === 'breakdown') &&
      (e.sourceItemId === itemId || e.targetItemId === itemId) &&
      e.status !== 'dissolved',
  );
  if (activeCompositionEdges.length > 0) {
    outcomes.push({
      level: 'block',
      reason: 'DISSOLUTION_RELATIONSHIP_REVIEW_REQUIRED',
      message: `Item has ${activeCompositionEdges.length} active composition relationship(s) (aggregation/breakdown). Re-home or dissolve composition children before dissolution (step 3 of dissolution sequence).`,
      gate: 1,
      involvedItemIds: [itemId],
      involvedRelationshipIds: activeCompositionEdges.map((e) => e.id),
    });
  }

  // Active elaboration edges
  const activeElEdges = edges.filter(
    (e) =>
      e.type === 'elaboration' &&
      (e.sourceItemId === itemId || e.targetItemId === itemId) &&
      e.status !== 'dissolved',
  );
  if (activeElEdges.length > 0) {
    outcomes.push({
      level: 'block',
      reason: 'DISSOLUTION_RELATIONSHIP_REVIEW_REQUIRED',
      message: `Item has ${activeElEdges.length} active elaboration relationship(s). Review and re-home or dissolve elaboration children before dissolution (step 4 of dissolution sequence).`,
      gate: 1,
      involvedItemIds: [itemId],
      involvedRelationshipIds: activeElEdges.map((e) => e.id),
    });
  }

  // Active dependency edges
  const activeDepEdges = edges.filter(
    (e) =>
      e.type === 'dependency' &&
      (e.sourceItemId === itemId || e.targetItemId === itemId) &&
      e.status !== 'dissolved',
  );
  if (activeDepEdges.length > 0) {
    outcomes.push({
      level: 'block',
      reason: 'DISSOLUTION_RELATIONSHIP_REVIEW_REQUIRED',
      message: `Item has ${activeDepEdges.length} active dependency relationship(s). Transfer, satisfy, or record each as unresolvable before dissolution (step 5 of dissolution sequence).`,
      gate: 1,
      involvedItemIds: [itemId],
      involvedRelationshipIds: activeDepEdges.map((e) => e.id),
    });
  }

  // Active collection memberships
  const activeCollections = itemGroups.filter(
    (g) => g.type === 'collection' && g.status !== 'dissolved',
  );
  if (activeCollections.length > 0) {
    outcomes.push({
      level: 'block',
      reason: 'DISSOLUTION_RELATIONSHIP_REVIEW_REQUIRED',
      message: `Item is a member of ${activeCollections.length} Collection(s). Transfer collection memberships to successor or remove before dissolution (step 6 of dissolution sequence).`,
      gate: 1,
      involvedItemIds: [itemId],
      involvedRelationshipIds: activeCollections.map((g) => g.id),
    });
  }

  // Active Contains edges (reporting hierarchy — canon 07A §9 step 7)
  const activeContainsEdges = edges.filter(
    (e) =>
      e.type === 'contains' &&
      (e.sourceItemId === itemId || e.targetItemId === itemId) &&
      e.status !== 'dissolved',
  );
  if (activeContainsEdges.length > 0) {
    outcomes.push({
      level: 'block',
      reason: 'DISSOLUTION_RELATIONSHIP_REVIEW_REQUIRED',
      message: `Item has ${activeContainsEdges.length} active Contains relationship(s). Re-home containment children or dissolve Contains edges before dissolution (step 7 of dissolution sequence).`,
      gate: 1,
      involvedItemIds: [itemId],
      involvedRelationshipIds: activeContainsEdges.map((e) => e.id),
    });
  }

  return outcomes;
}
